package co.maytes.standupbot.dashboard.components.layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.motion.Presence
import co.maytes.standupbot.dashboard.components.motion.presenceClasses
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.shared.motion.PresenceState
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px

@Composable
fun MobileNav(
    onDismiss: () -> Unit,
    open: Boolean = true,
    content: (@Composable () -> Unit)? = null,
) {
    Presence(visible = open, enterMs = Motion.DRAWER_IN_MS, exitMs = Motion.DRAWER_OUT_MS) { presence ->
        MobileNavLayer(presence = presence, onDismiss = onDismiss, content = content)
    }
}

@Composable
private fun MobileNavLayer(
    presence: PresenceState,
    onDismiss: () -> Unit,
    content: (@Composable () -> Unit)?,
) {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val user = auth.currentUser

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Color.rgba(0, 0, 0, 0.4f))
            .presenceClasses("sb-drawer-scrim", presence)
            .onClick { onDismiss() }
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "${ZIndex.nav}")
            },
    ) {
        Column(
            modifier = Modifier
                .width(288.px)
                .fillMaxHeight()
                .classNames("sb-surface")
                .presenceClasses("sb-drawer", presence)
                .padding(leftRight = Spacing.sm, top = Spacing.md, bottom = Spacing.lg)
                .onClick { event -> event.stopPropagation() },
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = Spacing.md, bottom = Spacing.sm),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                val workspaceName = user?.workspace?.name
                if (workspaceName != null) {
                    SpanText(
                        text = workspaceName,
                        modifier = Modifier
                            .fontSize(Typography.body.size)
                            .fontWeight(600)
                            .color(Palette.textPrimary)
                            .styleModifier { property("font-family", Typography.FONT_MONO) },
                    )
                } else {
                    SpanText(
                        text = "Menu",
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .fontWeight(600)
                            .color(Palette.textTertiary)
                            .styleModifier {
                                property("text-transform", "none")
                                property("letter-spacing", "0.08em")
                            },
                    )
                }
                IconButton(
                    ariaLabel = "Close menu",
                    modifier = Modifier.color(Palette.textSecondary),
                    onClick = { onDismiss() },
                ) { LucideX(size = 18) }
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(Spacing.xxs),
            ) {
                if (content != null) content() else NavList()
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("flex", "1 1 auto") },
            )

            if (user != null) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(Palette.borderSubtle)
                        .margin(topBottom = Spacing.sm),
                )
                Column(modifier = Modifier.fillMaxWidth().padding(leftRight = Spacing.md, bottom = Spacing.sm)) {
                    SpanText(
                        text = user.displayName,
                        modifier = Modifier
                            .fontSize(Typography.body.size)
                            .fontWeight(600)
                            .color(Palette.textPrimary),
                    )
                    user.email?.let { email ->
                        SpanText(
                            text = email,
                            modifier = Modifier
                                .margin(top = 2.px)
                                .fontSize(Typography.caption.size)
                                .color(Palette.textSecondary)
                                .styleModifier { property("font-family", Typography.FONT_MONO) },
                        )
                    }
                }
                Box(modifier = Modifier.padding(leftRight = Spacing.md)) {
                    Button(
                        label = "Sign out",
                        variant = ButtonVariant.Secondary,
                        onClick = {
                            scope.launch {
                                auth.signOut()
                                ui.showToast("Signed out", ToastKind.Success)
                                ctx.router.navigateTo("/")
                            }
                        },
                    )
                }
            }
        }
    }
}
